import React, { useState, useRef, useEffect, useCallback } from "react";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, X, Send, Compass } from "lucide-react";

export default function MasterFab() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Seed the greeting (and refresh it if the language changes before any chat)
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 0 || (prev.length === 1 && prev[0].seed)) {
        return [{ role: "assistant", content: t("master.greeting"), seed: true }];
      }
      return prev;
    });
  }, [t, lang]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => { if (open) scrollToBottom(); }, [messages, busy, open, scrollToBottom]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const history = next
        .filter((m) => !m.seed)
        .slice(-6)
        .map((m) => ({ role: m.role, content: m.content }));
      const res = await api.post("/master/chat", { message: text, history, lang });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: t("master.error"), error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Floating trigger (bottom-left to avoid the WhatsApp fab on the right) */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t("master.open")}
          data-testid="master-fab"
          className="floating-daily-master-button fixed bottom-24 right-5 z-50 flex items-center gap-2 rounded-full border border-[hsla(var(--brand-gold),0.55)] bg-card/90 px-4 py-3 text-foreground shadow-[0_18px_45px_rgba(20,18,12,0.12)] backdrop-blur card-lift"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[hsla(var(--brand-gold-soft),0.5)]">
            <Sparkles className="h-4 w-4 text-[hsl(var(--brand-bronze))]" />
          </span>
          <span className="hidden sm:inline text-sm font-semibold">{t("master.open")}</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="master-panel"
          className="fixed z-[60] bottom-4 left-3 right-3 sm:left-auto sm:right-5 sm:w-[380px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden"
          style={{ maxHeight: "76vh" }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-[hsl(var(--primary))] text-primary-foreground">
            <div className="h-9 w-9 rounded-full bg-[hsl(var(--brand-gold-soft))] flex items-center justify-center shrink-0">
              <Compass className="h-5 w-5 text-[hsl(var(--brand-bronze))]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-semibold leading-tight">{t("master.title")}</div>
              <div className="text-[11px] opacity-80 truncate">{t("master.subtitle")}</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label={t("common.close")} data-testid="master-close" className="p-1 rounded-md hover:bg-white/10">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-[hsl(var(--secondary))]/40" data-testid="master-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm leading-6 whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-[hsl(var(--primary))] text-primary-foreground rounded-br-sm"
                      : "bg-card border border-border text-foreground rounded-bl-sm"
                  }`}
                  data-testid={`master-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="master-typing">
                <div className="bg-card border border-border rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-muted-foreground italic">
                  {t("master.thinking")}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-2.5 border-t border-border flex items-center gap-2 bg-card">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={t("master.placeholder")}
              disabled={busy}
              data-testid="master-input"
              className="flex-1"
            />
            <Button size="icon" onClick={send} disabled={busy || !input.trim()} data-testid="master-send" className="bg-[hsl(var(--primary))] text-primary-foreground shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
